/**
 * Marketing pixel parameter collector for B2M Tracker.
 *
 * Reads Meta and Google identifiers that the customer's installed pixels
 * expose to the browser and normalises them to reserved properties:
 *   $fbp           ← _fbp cookie set by Meta Pixel
 *   $fbc           ← _fbc cookie, or derived from URL fbclid in fb.1.<ts>.<fbclid> form
 *   $gclid         ← URL gclid, persisted to localStorage + cookie for the session
 *   $ga_client_id  ← clientId portion of the _ga cookie set by Google Analytics
 *
 * Missing values are omitted from the returned object — callers send nothing
 * rather than a null/empty field.
 */

import { getCookie, setCookie } from './cookie-utils.js';

const STORAGE_GCLID = 'b2m_gclid';
const STORAGE_FBC = 'b2m_fbc';
const COOKIE_GCLID = 'b2m_gclid';
const COOKIE_FBC = 'b2m_fbc';

/**
 * Parse the _ga cookie (format: GA1.<scope>.<clientId>.<timestamp>) and
 * return the `<clientId>.<timestamp>` portion that Google Analytics uses
 * as the Client ID. Returns null on any unexpected format.
 */
function parseGaClientId(raw) {
  if (!raw) return null;
  const parts = raw.split('.');
  if (parts.length < 4 || !parts[0].startsWith('GA')) return null;
  const cid = parts.slice(2).join('.');
  return cid || null;
}

/**
 * Persist a freshly-captured pixel param to BOTH localStorage (cross-tab,
 * primary) and a cookie (subdomain reach). Failures are swallowed — the
 * SDK must not crash if storage is blocked.
 */
function persist(storage, lsKey, cookieKey, value) {
  if (!value) return;
  try { storage.setItem(lsKey, value); } catch (e) { /* ignore */ }
  setCookie(cookieKey, value);
}

/**
 * Capture URL-sourced pixel params (gclid, fbclid) on init/page load and
 * persist them so subsequent events in the same session retain the IDs
 * even after the user navigates away from the landing URL.
 *
 * Called from b2m.js _trackCampaignParams().
 */
export function capturePixelParamsFromUrl(storage) {
  if (typeof window === 'undefined' || !window.location) return;
  try {
    const params = new URLSearchParams(window.location.search);

    const gclid = params.get('gclid');
    if (gclid) persist(storage, STORAGE_GCLID, COOKIE_GCLID, gclid);

    // If Meta Pixel hasn't yet written _fbc but a fbclid landed in the URL,
    // synthesise the _fbc value Meta would have produced. Format documented
    // by Meta: fb.<subdomainIndex>.<timestamp_ms>.<fbclid>. We use a fixed
    // subdomainIndex of 1 (most common for .com domains).
    const fbclid = params.get('fbclid');
    if (fbclid && !getCookie('_fbc')) {
      const derived = `fb.1.${Date.now()}.${fbclid}`;
      persist(storage, STORAGE_FBC, COOKIE_FBC, derived);
    }
  } catch (e) {
    /* ignore — never let pixel capture crash tracking */
  }
}

/**
 * Read all four pixel identifiers fresh on each batch send. Cookies set
 * by Meta Pixel / GA may not be present at SDK init but appear later, so
 * we re-read on every flush rather than caching.
 *
 * Returns an object containing only the keys that resolved to a value.
 */
export function collectPixelParams(storage) {
  const out = {};

  try {
    const fbp = getCookie('_fbp');
    if (fbp) out.$fbp = fbp;
  } catch (e) { /* ignore */ }

  try {
    // Prefer the live Meta cookie; fall back to whatever we derived/persisted.
    let fbc = getCookie('_fbc');
    if (!fbc) {
      try { fbc = storage && storage.getItem(STORAGE_FBC); } catch (e) { /* ignore */ }
    }
    if (!fbc) fbc = getCookie(COOKIE_FBC);
    if (fbc) out.$fbc = fbc;
  } catch (e) { /* ignore */ }

  try {
    let gclid = null;
    if (typeof window !== 'undefined' && window.location) {
      gclid = new URLSearchParams(window.location.search).get('gclid');
    }
    if (!gclid) {
      try { gclid = storage && storage.getItem(STORAGE_GCLID); } catch (e) { /* ignore */ }
    }
    if (!gclid) gclid = getCookie(COOKIE_GCLID);
    if (gclid) out.$gclid = gclid;
  } catch (e) { /* ignore */ }

  try {
    const ga = getCookie('_ga');
    const cid = parseGaClientId(ga);
    if (cid) out.$ga_client_id = cid;
  } catch (e) { /* ignore */ }

  return out;
}
